import React, { useContext, useEffect, useState } from "react";
import { centsToCurrency, getEvent } from "../services/firebase";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Carousel from "react-material-ui-carousel";
import { CartContext } from "../context/CartContext";
import Container from "@mui/material/Container";
import EventDateSelector from "../components/EventDateSelector";
import ExpandableText from "../components/ExpandableText";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import PlaceTwoTone from "@mui/icons-material/PlaceTwoTone";
import { QuantitySelector } from "../components/QuantitySelector";
import ReactHtmlParser from "react-html-parser";
import Share from "../components/Share";
import ShoppingCartCheckoutTwoTone from "@mui/icons-material/ShoppingCartCheckoutTwoTone";
import SocialMetaTags from "../components/header/SocialMetaTags";
import Typography from "@mui/material/Typography";
import firebase from "firebase/compat/app";
import { logEvent } from "../services/firebase";

const optionsDate = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
};
const optionsTime = { hour: "numeric", minute: "2-digit", hour12: true };

export const EventDetail = () => {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const { addToCart, removeFromCart, getCartItems } = useContext(CartContext);

  const [event, setEvent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    setIsError(false);
    getEvent(eventSlug).then((event) => {
      if (event && !event.error) {
        event.tickets = event.tickets.map((ticket) => ({
          ...ticket,
          id: ticket.sku,
        }));
        event.additionalImages = event.additionalImages || [];
        setEvent(event);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    });
  }, [eventSlug]);

  useEffect(() => {
    if (event.tickets?.length > 0 && !event.baseTickets) {
      event.baseTickets = [...event.tickets];
    }
  }, [event]);

  const renderDateSelector = () => {
    // Add more thorough validation
    if (
      !event ||
      !event.instances ||
      !Array.isArray(event.instances) ||
      event.instances.length <= 1
    ) {
      return null;
    }

    // Make sure the instances have the necessary data structure
    const validInstances = event.instances.filter(
      (instance) =>
        instance &&
        instance.id &&
        instance.startDate &&
        instance.startDate.seconds
    );

    if (validInstances.length <= 1) {
      return null;
    }

    // Initialize base tickets if not already done
    if (event.tickets?.length > 0 && !event.baseTickets) {
      event.baseTickets = [...event.tickets];
    }

    return (
      <EventDateSelector
        instances={validInstances}
        onInstanceChange={handleInstanceChange}
        selectedInstance={selectedInstance} // Pass the selected instance from parent state
      />
    );
  };

  const handleInstanceChange = (index) => {
    if (!event.instances || index >= event.instances.length) return;

    setSelectedInstance(index);

    const instance = event.instances[index];
    const instanceDate = new Date(instance.startDate.seconds * 1000);

    // Format for display
    const formattedDateTime = instanceDate.toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

    // Make sure baseTickets exists before trying to map it
    if (!event.baseTickets) {
      // If baseTickets doesn't exist yet, initialize it with current tickets first
      if (event.tickets && event.tickets.length > 0) {
        event.baseTickets = [...event.tickets];
      } else {
        // Safety fallback - initialize as empty array if no tickets
        event.baseTickets = [];
      }
    }

    // Update tickets with new instance info (with safety check)
    if (event.baseTickets && event.baseTickets.length > 0) {
      event.tickets = event.baseTickets.map((ticket) => ({
        ...ticket,
        productType: "ticket",
        sku: `${ticket.id}-${instance.id}`, // Use instance ID directly for clarity
        eventId: event.id,
        eventName: event.name,
        eventInstanceId: instance.id,
        instanceDateTime: formattedDateTime,
        startDate: instance.startDate,
        endDate: instance.endDate,
      }));
    }

    // Update display dates
    event.startDate = instance.startDate;
    event.endDate = instance.endDate;
    event.selectedInstanceIndex = index;
  };

  const addTicketToCart = (ticket) => {
    const currentInstance = event.instances[selectedInstance];
    const instanceDate = new Date(currentInstance.startDate.seconds * 1000);

    // Format date for display
    const instanceDateTime = instanceDate.toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

    // Create a ticket with all required instance data
    const ticketWithInstance = {
      ...ticket,
      productType: "ticket",
      eventId: event.id,
      eventName: event.name,
      eventInstanceId: currentInstance.id,
      instanceDateTime: instanceDateTime,
      startDate: currentInstance.startDate,
      endDate: currentInstance.endDate,
    };

    // Call the cart's addToCart function
    addToCart(ticketWithInstance);
  };

  const getTicketSkuForInstance = (ticket, instanceId) => {
    return `${ticket.id}-instance-${instanceId}${
      ticket.selectedOptions && ticket.selectedOptions.length > 0
        ? `-${ticket.selectedOptions.join("-")}`
        : ""
    }`;
  };

  // Add a function to find cart item for a given ticket
  const findTicketInCart = (ticket) => {
    const cartItems = getCartItems();
    const currentInstanceId = event.instances[selectedInstance].id;
    const expectedSku = getTicketSkuForInstance(ticket, currentInstanceId);

    return cartItems.find((item) => item.sku === expectedSku);
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }
  if (isError) {
    return (
      <p>
        Sorry, we couldn't find that event. Check our
        <a href="/events"> Event List</a>
      </p>
    );
  }

  const formattedDate = event.startDate
    ? event.startDate.toDate().toLocaleDateString("en-US", optionsDate)
    : "No Upcoming Dates";
  const formattedStartTime = event.startDate
    ? event.startDate.toDate().toLocaleTimeString("en-US", optionsTime)
    : "";
  const formattedEndTime = event.startDate
    ? event.endDate.toDate().toLocaleTimeString("en-US", optionsTime)
    : "";

  const checkout = () => {
    const cartItems = getCartItems();
    if (cartItems.length === 0) {
      addTicketToCart(event.tickets[0]);
    }
    navigate("/checkout");
  };

  const allImages = [event.mainImage, ...event.additionalImages];
  const defaultImage = allImages.length > 0 ? allImages[0] : null;

  return (
    <Container fixed maxWidth="md">
      <SocialMetaTags
        title={`${event.name} at The Cola St. Louis`}
        description={event.summary}
        image={defaultImage.src}
        page={`events/${eventSlug}`}
        siteName="The Cola St. Louis"
      />
      <Box sx={{ my: 2 }}>
        <Grid container spacing={0} sx={{ pb: 20 }}>
          <Grid item xs={12} mb={2}>
            <Typography variant="h3">{event.name}</Typography>
          </Grid>
          <Grid xs={12} md={6} item>
            {allImages.length ? (
              <Carousel animation="slide">
                {allImages.map((image, index) => (
                  <Paper key={index} elevation={3}>
                    <img
                      src={image.src}
                      alt={image.title}
                      style={{ width: "100%", height: "100%", minHeight: 250 }}
                    />
                  </Paper>
                ))}
              </Carousel>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2 }}>
              {event.startDate ? (
                <Typography variant="h5">
                  <strong>{formattedDate}</strong> <br />
                  from {formattedStartTime} to {formattedEndTime}
                </Typography>
              ) : (
                <Typography variant="h5">No Upcoming Dates</Typography>
              )}

              {renderDateSelector()}
              <Box sx={{ mt: 2, display: "flex" }}>
                <PlaceTwoTone fontSize="large" />
                <Typography variant="body2" ml={2}>
                  <strong>{event.location?.name}</strong>
                  <br />
                  <a href="https://www.google.com/maps/dir//2834+Cherokee+St,+St.+Louis,+MO+63118/@38.59383,-90.3139245,12z/">
                    {event.location?.address}
                  </a>
                </Typography>
              </Box>

              <Box p={2}>{ReactHtmlParser(event?.description)}</Box>

              <Share
                url={event.url}
                title={event.name}
                description={event.summary}
                hashtags={["WhereBudsMeet", "ColaSTL"]}
                media={defaultImage}
                onShare={(platform) =>
                  logEvent(firebase.analytics.EventName.SHARE, {
                    method: platform,
                    content_type: "event",
                    item_id: eventSlug,
                  })
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Grid
        container
        sx={{
          position: "fixed",
          bottom: 72,
          left: 0,
          right: 0,
          p: 1,
          px: 2,
          bgcolor: "grey.200",
          borderTop: "1px solid",
          borderTopColor: "grey.300",
        }}
      >
        {event.tickets.length > 0 && (
          <>
            <Grid xs={12} item>
              <Box>
                {event.tickets.map((ticket) => (
                  <Box
                    key={ticket.sku}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                      <Typography variant="h6">{ticket.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ticket.instanceDateTime}
                      </Typography>
                      <ExpandableText text={ticket?.description} />
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2">
                        {centsToCurrency(ticket.amount)}
                      </Typography>
                    </Box>
                    <QuantitySelector
                      item={
                        findTicketInCart(ticket) || {
                          ...ticket,
                          quantity: 0,
                        }
                      }
                      remove={() => {
                        // Find the matching item in cart and remove it
                        const cartItem = findTicketInCart(ticket);
                        if (cartItem) {
                          removeFromCart(cartItem);
                        }
                      }}
                      add={() => addTicketToCart(ticket)}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid xs={12} item textAlign={"right"}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={checkout}
                endIcon={<ShoppingCartCheckoutTwoTone />}
              >
                Checkout
              </Button>
            </Grid>
          </>
        )}
        {event.ticketlink && (
          <Grid xs={12} item textAlign={"center"}>
            <Button
              component={"a"}
              href={event.ticketlink}
              target="_blank"
              size="large"
              variant="contained"
              color="primary"
              endIcon={<ShoppingCartCheckoutTwoTone />}
            >
              Buy Tickets
            </Button>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};
